import {Avatar, Button, IconButton, Menu, MenuItem, useMediaQuery} from '@material-ui/core';
import {AlertPayload, getUserAlerts} from 'Core/ducks/alerts';
import {ModalState, showModal} from 'Core/ducks/modal';
import {toPageEither, isLoaded} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {sequenceT} from 'fp-ts/lib/Apply';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {constant} from 'fp-ts/lib/function';
import {IO, io} from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {ReactNode, useEffect} from 'react';
import {connect, useSelector, useDispatch} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import MenuStateProvider from 'Shared/components/MenuStateProvider';
import RequireScope from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {Scope} from 'Shared/constants/Scope';
import Badge from 'Shared/utilities/Badge';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {prop} from 'Shared/utilities/prop';
import * as AgencyAdministrationActions from 'Users/ducks/agentAdministration';
import {setSelectedAgency} from 'Users/ducks/userProfile';
import {getFullName} from 'Users/models/Agent';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {UserRouteConstants} from 'Users/Routes';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './AccountMenu.sass';
import {bindActionCreators} from 'redux';
import {handleAlerts} from 'Core/components/Startup';
import {fromLocalStorage, LocalStorageKey} from 'Shared/utilities/localStorage';
import {logoutFromAWD} from 'Shared/utilities/Auth';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {createSnackbar} from 'Core/ducks/snackbar';
import {resetLogin} from 'ApplicationEntry/ducks/AWDLogin';
import * as CustomerSupportActions from 'Users/ducks/CustomerSupport';

type AccountMenuProps = {
  onLogout: () => void;
  showModal: typeof showModal;
  setSelectedAgency: typeof setSelectedAgency;
} & WithClaimsProps &
  typeof AgencyAdministrationActions &
  ReturnType<typeof mapStateToProps> &
  RouteComponentProps;

const renderAlertIcon = (onClick: IO<void>, alerts: Page<AlertPayload>) => (
  <IconButton size="small" className="alert-button" onClick={onClick}>
    <Badge count={alerts.totalCount}>
      <i style={{color: 'white'}} className="fa fa-bell" />
    </Badge>
  </IconButton>
);

const getAlertsCount = (alertsPage: AccountMenuProps['userAlerts']) =>
  pipe(alertsPage, toPageEither, foldEither(constant(0), prop('totalCount')));

const onShowAlerts = (alerts: Page<AlertPayload>, displayModal: typeof showModal) => () => {
  displayModal(
    ModalState.Alerts({
      messages: alerts.result,
      onDismissAlerts: () => {},
    })
  );
};
const AccountMenu = (props: AccountMenuProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const scopes = useSelector(selectUserScopes);
  const dispatch = useDispatch();
  const {userAlerts} = useSelector((state: ReducerState) => state.alerts);
  const {modalState} = useSelector((state: ReducerState) => state.modal);
  const {showModal: boundShowModal} = bindActionCreators({showModal}, dispatch);
  const isLoggedInToAWD = fromLocalStorage(LocalStorageKey.AWDDSTSession)();

  useEffect(() => {
    dispatch(getUserAlerts());
  }, []);

  useEffect(() => {
    if (isLoaded(userAlerts) && modalState.tag === ModalState.None().tag) {
      handleAlerts(userAlerts, boundShowModal)();
    }
  }, [userAlerts]);

  const fullName = getFullName(props.claims);
  const AlertsIcon = () =>
    pipe(
      props.userAlerts,
      toPageEither,
      foldEither(
        () => null,
        alerts => renderAlertIcon(onShowAlerts(alerts, props.showModal), alerts)
      )
    );

  const getAlertItemLabel = () => {
    const alertsCount = getAlertsCount(props.userAlerts);
    return `Alerts (${alertsCount})`;
  };
  const getTaxId = (agency: AgentAgencyClaim) => `(${agency.maskedIdentifier.replace(/X/g, '')})`;

  return (
    <div className="account-menu">
      <AlertsIcon />
      <MenuStateProvider>
        {({close, open, anchor}) => (
          <>
            {isDesktop ? (
              <Button
                id="account-icon-button"
                color="inherit"
                className="account-menu__button"
                onClick={event => open(event.currentTarget)}
              >
                <span className="account-menu__user-name">{fullName}</span>
                <span className="account-menu__tax-identifier">
                  {pipe(
                    props.selectedAgency,
                    O.fold(
                      () => '' as ReactNode,
                      agency => (
                        <>
                          <span>{getTaxId(agency)}</span>
                          {
                            <span>
                              {agency.isNewYork ? (
                                <span>
                                  - <strong className="account-menu__new-york-symbol">NY</strong>
                                </span>
                              ) : null}
                            </span>
                          }
                        </>
                      )
                    )
                  )}
                </span>
                <i className="fa fa-angle-down" />
              </Button>
            ) : (
              <IconButton onClick={event => open(event.currentTarget)}>
                <Avatar className="account-menu__mobile-button">
                  {props.claims.firstName[0] + props.claims.lastName[0]}
                </Avatar>
              </IconButton>
            )}
            <Menu
              id="account-menu"
              getContentAnchorEl={null}
              anchorEl={anchor}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
              transformOrigin={{
                horizontal: 'center',
                vertical: 'top',
              }}
              open={Boolean(anchor)}
              onClose={close}
            >
              <MenuItem
                disabled={getAlertsCount(props.userAlerts) === 0}
                className="account-menu__alerts-item"
                onClick={pipe(
                  props.userAlerts,
                  toPageEither,
                  foldEither(
                    () => NoOp,
                    alerts => sequenceT(io)(onShowAlerts(alerts, props.showModal), close)
                  )
                )}
              >
                {getAlertItemLabel()}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  props.history.push(UserRouteConstants.PROFILE);
                }}
              >
                User Profile
              </MenuItem>
              {process.env.REACT_APP_TOGGLE_AGENT_ACCESS === 'true' && (
                <RequireScope scopes={scopes} scope={Scope.SsoAccess}>
                  <MenuItem
                    onClick={() => {
                      close();
                      props.history.push(UserRouteConstants.SSO_ACCOUNTS);
                    }}
                  >
                    Single Sign-On
                  </MenuItem>
                </RequireScope>
              )}
              {props.claims.userTypes &&
                props.claims.userTypes.map(
                  UserTypeClaims.match({
                    [UserTypeClaimKey.AgentSite]: agentClaims =>
                      agentClaims.agencyAssociations.length > 0 && (
                        <MenuItem
                          key={`${agentClaims.firstName} ${agentClaims.recruiterCode}`}
                          onClick={() => {
                            close();
                            props.showModal(
                              ModalState.SwitchTaxId({agencyAssociations: agentClaims.agencyAssociations})
                            );
                          }}
                        >
                          Switch Accounts
                        </MenuItem>
                      ),
                    [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims => {
                      const hasAgentUserType = props.claims.userTypes.some(x => x.$type === UserTypeClaimKey.AgentSite);
                      return (
                        !hasAgentUserType &&
                        enrollTrackClaims.agencyAssociations &&
                        enrollTrackClaims.agencyAssociations.length > 0 &&
                        enrollTrackClaims.exclusivityStatus.name === 'Internal' && (
                          <MenuItem
                            key={`${enrollTrackClaims.firstName} ${enrollTrackClaims.LastName}`}
                            onClick={() => {
                              close();
                              props.showModal(
                                ModalState.SwitchTaxId({agencyAssociations: enrollTrackClaims.agencyAssociations})
                              );
                            }}
                          >
                            Switch Accounts
                          </MenuItem>
                        )
                      );
                    },
                    default: () => null,
                  })
                )}
              {props.claims.isImpersonating &&
                props.claims.userTypes &&
                props.claims.userTypes.map(
                  UserTypeClaims.match({
                    [UserTypeClaimKey.AgentSite]: agentClaims =>
                      agentClaims.agencyAssociations.length > 0 && (
                        <MenuItem
                          id="agent-list-menu-item"
                          onClick={() => {
                            close();
                            if (props.claims.originalUserName) {
                              props.stopImpersonation(props.claims.originalUserName);
                            }
                          }}
                        >
                          Return To Agent List
                        </MenuItem>
                      ),
                    default: () => null,
                  })
                )}
              {props.claims.isImpersonating ? (
                <MenuItem
                  id="customer-support-list-menu-item"
                  onClick={() => {
                    close();
                    sessionStorage.removeItem('statusNameContent');
                    if (props.claims.originalUserName) {
                      dispatch(CustomerSupportActions.stopCustomerSupportImpersonation(props.claims.originalUserName));
                    }
                  }}
                >
                  Return To Customer Support List
                </MenuItem>
              ) : (
                <MenuItem
                  id="logout-menu-item"
                  onClick={() => {
                    close();
                    props.onLogout();
                  }}
                >
                  Logout
                </MenuItem>
              )}
              {isLoggedInToAWD && (
                <MenuItem
                  onClick={() => {
                    close();
                    logoutFromAWD();
                    sessionStorage.removeItem('statusNameContent');
                    sessionStorage.clear();
                    window.location.href = CoreRouteConstants.DASHBOARD;
                    dispatch(resetLogin());
                    dispatch(createSnackbar('Successfully Logged Out From AWD'));
                  }}
                >
                  Logout from AWD
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </MenuStateProvider>
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  selectedAgency: state.user.profile.selectedAgency,
  userAlerts: state.alerts.userAlerts,
});

export default connect(mapStateToProps, {
  ...AgencyAdministrationActions,
  showModal,
  setSelectedAgency,
})(withUserClaims(withRouter(AccountMenu)));
